import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ActiveProduct,
  BillingService,
  SubscribableProduct,
  SubscriptionStatus,
} from '../../../../services/billing.service';
import { BaseComponent } from '../../../../../../../core/shared/common/base.component';
import { AddSubscriptionDialogComponent } from './components/add-subscription-dialog/add-subscription-dialog.component';

@Component({
  selector: 'app-active-products',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, AddSubscriptionDialogComponent],
  templateUrl: './active-products.component.html',
  styleUrl: './active-products.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActiveProductsComponent extends BaseComponent {
  private billingService = inject(BillingService);

  public organizationId = input.required<number>();

  public products = signal<ActiveProduct[]>([]);
  public isLoading = signal<boolean>(true);

  public editingProductId = signal<number | null>(null);
  public contractedCount = new FormControl<number>(0, { nonNullable: true });
  public editStatus = new FormControl<SubscriptionStatus>('active', { nonNullable: true });

  // +Add Subscription (F5): the base services the org's parent can resell.
  public subscribableProducts = signal<SubscribableProduct[]>([]);
  public canAdd = computed(() => this.subscribableProducts().length > 0);
  public showAddDialog = signal<boolean>(false);
  public savingSubscription = signal<boolean>(false);

  // Statuses selectable per customer-subscription row (incl. cancel).
  public readonly statusOptions: SubscriptionStatus[] = [
    'active',
    'scheduled',
    'expired',
    'cancelled',
  ];

  constructor() {
    super();
    effect(
      () => {
        const id = this.organizationId();
        if (!id) return;
        this.load(id);
      },
      { allowSignalWrites: true }
    );
  }

  private load(id: number): void {
    this.isLoading.set(true);
    this.billingService
      .getActiveProducts(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.products.set(res.products);
          this.isLoading.set(false);
        },
        error: (err) => {
          console.error('Failed to load subscriptions:', err);
          this.isLoading.set(false);
        },
      });
    this.billingService
      .getSubscribableProducts(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => this.subscribableProducts.set(res.products),
        error: (err) => console.error('Failed to load subscribable products:', err),
      });
  }

  // --- +Add Subscription ------------------------------------------------------

  public openAddDialog(): void {
    this.showAddDialog.set(true);
  }

  public closeAddDialog(): void {
    this.showAddDialog.set(false);
  }

  public onAddSubscription(payload: { product_id: string; contracted_count: number }): void {
    this.savingSubscription.set(true);
    this.billingService
      .createSubscription(this.organizationId(), payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.savingSubscription.set(false);
          this.showAddDialog.set(false);
          this.load(this.organizationId()); // refetch so the new row appears, rate-resolved
        },
        error: (err) => {
          console.error('Failed to add subscription:', err);
          this.savingSubscription.set(false);
        },
      });
  }

  // --- inline edit: contracted count (B3) + status / cancel (F5) ---------------

  /** True for rows whose status is editable (admin-entered subs; Zenskar rows are read-only). */
  public isCustomerSubscription(product: ActiveProduct): boolean {
    return product.source === 'customer_subscription';
  }

  public startEdit(product: ActiveProduct): void {
    this.contractedCount.setValue(product.contracted_count);
    this.editStatus.setValue(product.status ?? 'active');
    this.editingProductId.set(product.id);
  }

  public cancelEdit(): void {
    this.editingProductId.set(null);
  }

  public saveEdit(product: ActiveProduct): void {
    const nextCount = this.contractedCount.value;
    const editable = this.isCustomerSubscription(product);
    const nextStatus = editable ? this.editStatus.value : product.status;
    const prevCount = product.contracted_count;
    const prevStatus = product.status;

    // Optimistic update (count + status, for customer subscriptions), then persist both in one
    // PATCH. `source` routes the write: Zenskar override (count only) vs the CustomerSubscription.
    this.products.update((items) =>
      items.map((p) =>
        p.id === product.id ? { ...p, contracted_count: nextCount, status: nextStatus } : p
      )
    );
    this.editingProductId.set(null);

    const changes: { contracted_count?: number; status?: SubscriptionStatus } = {
      contracted_count: nextCount,
    };
    if (editable) changes.status = this.editStatus.value;

    this.billingService
      .updateSubscriptionRow(this.organizationId(), product.id, product.source ?? 'zenskar', changes)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: (err) => {
          console.error('Failed to save subscription:', err);
          this.products.update((items) =>
            items.map((p) =>
              p.id === product.id
                ? { ...p, contracted_count: prevCount, status: prevStatus }
                : p
            )
          );
        },
      });
  }
}
